import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';

// Ticket 202: the owner's feature switches. The list comes from the server
// registry; a switch is off until the owner turns it on, and it cannot be turned
// on while the platform or the setup for it is missing (the server refuses too).
export default function FeaturesTab() {
  const [items, setItems] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get('/v2/org/features')
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => {
        setItems([]);
        toast({ title: 'Could not load features', description: errorMessage(e) });
      });
  }, []);

  const flip = async (item) => {
    setBusyKey(item.key);
    try {
      const { data } = await api.put(`/v2/org/features/${item.key}`, { enabled: !item.enabled });
      setItems(data.items || []);
    } catch (e) {
      toast({ title: `Could not change ${item.label}`, description: errorMessage(e) });
    } finally {
      setBusyKey(null);
    }
  };

  if (!items) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="features-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm max-w-2xl divide-y divide-slate-100" data-testid="features-tab">
      <p className="px-5 py-3 text-sm text-slate-500">
        Everything here is off until you turn it on. Only the owner sees this tab.
      </p>
      {items.map((f) => {
        const locked = !f.available && !f.enabled;
        return (
          <div key={f.key} className="px-5 py-4 flex items-start justify-between gap-4" data-testid={`feature-row-${f.key}`}>
            <div>
              <div className="font-medium text-slate-800">{f.label}</div>
              <div className="text-sm text-slate-500">{f.description}</div>
              {!f.available && (
                <div className="text-xs text-amber-700 mt-1" data-testid={`feature-reason-${f.key}`}>{f.unavailable_reason}</div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={f.enabled}
              aria-label={f.label}
              disabled={locked || busyKey === f.key}
              onClick={() => flip(f)}
              data-testid={`feature-switch-${f.key}`}
              className={`shrink-0 w-11 h-6 rounded-full transition relative disabled:opacity-40 ${f.enabled ? 'bg-emerald-600' : 'bg-slate-300'}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${f.enabled ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
